import * as THREE from 'three';
import type { BulletVisual, PrimitiveGeo } from '../content/types';

/**
 * Primitive geometry factory. All art is procedural: low-poly primitives with
 * flat shading. Geometries are cached and shared between InstancedMeshes.
 * GLTF drop-in: see render/models.ts (loads `visual.gltf` when present).
 */
const cache = new Map<string, THREE.BufferGeometry>();

function noiseDisplace(geo: THREE.BufferGeometry, amount: number, seed = 1): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const n = Math.sin(v.x * 4.1 + seed) * Math.cos(v.y * 3.7 + seed * 2) * Math.sin(v.z * 5.3 + seed * 3);
    v.multiplyScalar(1 + n * amount);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

export function primitiveGeometry(kind: PrimitiveGeo): THREE.BufferGeometry {
  const cached = cache.get(kind);
  if (cached) return cached;
  let g: THREE.BufferGeometry;
  switch (kind) {
    case 'sphere':
      g = new THREE.IcosahedronGeometry(0.5, 1);
      break;
    case 'box':
      g = new THREE.BoxGeometry(0.9, 0.9, 0.9);
      break;
    case 'cone':
      g = new THREE.ConeGeometry(0.5, 1.1, 6);
      g.rotateX(Math.PI / 2); // point along +z (we rotate to facing)
      break;
    case 'capsule':
      g = new THREE.CapsuleGeometry(0.35, 0.6, 2, 6);
      break;
    case 'octa':
      g = new THREE.OctahedronGeometry(0.55, 0);
      break;
    case 'spike': {
      const parts: THREE.BufferGeometry[] = [new THREE.IcosahedronGeometry(0.35, 0)];
      for (let i = 0; i < 6; i++) {
        const c = new THREE.ConeGeometry(0.12, 0.7, 4);
        const q = new THREE.Quaternion();
        const dir = new THREE.Vector3(Math.cos(i * 1.047), Math.sin(i * 1.047) * 0.6, Math.sin(i * 2.1)).normalize();
        q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
        c.applyQuaternion(q);
        c.translate(dir.x * 0.45, dir.y * 0.45, dir.z * 0.45);
        parts.push(c);
      }
      g = mergeGeometries(parts);
      break;
    }
    case 'disc':
      g = new THREE.CylinderGeometry(0.55, 0.55, 0.15, 12);
      break;
    case 'torus':
      g = new THREE.TorusGeometry(0.45, 0.14, 6, 12);
      g.rotateX(Math.PI / 2);
      break;
    case 'blob':
    default:
      g = noiseDisplace(new THREE.IcosahedronGeometry(0.55, 2), 0.18, 3);
      break;
  }
  if (g.index) g = g.toNonIndexed(); // flat shading look
  g.computeVertexNormals();
  cache.set(kind, g);
  return g;
}

export function bulletGeometry(kind: BulletVisual): THREE.BufferGeometry {
  const key = 'bullet_' + kind;
  const cached = cache.get(key);
  if (cached) return cached;
  let g: THREE.BufferGeometry;
  switch (kind) {
    case 'orb':
      g = new THREE.SphereGeometry(0.5, 8, 6);
      break;
    case 'shard':
      g = new THREE.OctahedronGeometry(0.5, 0);
      g.scale(0.6, 0.6, 1.4);
      break;
    case 'ring':
      g = new THREE.TorusGeometry(0.45, 0.15, 5, 10);
      g.rotateX(Math.PI / 2);
      break;
    case 'needle':
      g = new THREE.ConeGeometry(0.22, 1.3, 5);
      g.rotateX(Math.PI / 2);
      break;
    case 'blob':
      g = new THREE.IcosahedronGeometry(0.5, 0);
      break;
    case 'star':
    default:
      g = new THREE.OctahedronGeometry(0.6, 0);
      break;
  }
  cache.set(key, g);
  return g;
}

/** Minimal merge (positions+normals only) so we don't depend on BufferGeometryUtils. */
export function mergeGeometries(list: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const pos: number[] = [];
  const v = new THREE.Vector3();
  for (const g of list) {
    const ng = g.index ? g.toNonIndexed() : g;
    const p = ng.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i);
      pos.push(v.x, v.y, v.z);
    }
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.computeVertexNormals();
  return out;
}

/** 3-step toon gradient shared by all toon materials. */
let gradient: THREE.DataTexture | null = null;
export function toonGradient(): THREE.DataTexture {
  if (gradient) return gradient;
  const data = new Uint8Array([70, 140, 220, 255]);
  gradient = new THREE.DataTexture(data, 4, 1, THREE.RedFormat);
  gradient.minFilter = THREE.NearestFilter;
  gradient.magFilter = THREE.NearestFilter;
  gradient.needsUpdate = true;
  return gradient;
}

export function toonMaterial(color: number, emissive = 0, emissiveIntensity = 0): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ color, emissive, emissiveIntensity, gradientMap: toonGradient() });
}
